import { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import styled from 'styled-components';

import { PasswordDialog } from '@/components/PasswordDialog';
import routes from '@/constants/routes';
import {
  getAllRoadmapNodes,
  getFiltersByRoadmapId,
  getRoadmapsDropdownVisible,
  getRoadmapSections,
  getRoadmapsPopupVisible,
  getRoadmapsRoadmapVisible,
  getRoadmapTimePeriods,
} from '@/features/canvas/api';
import { getBudgetRoadmapVisible } from '@/features/canvas/api/budget-roadmap-visible';
import { getAllBudgetsForRoadmap } from '@/features/canvas/api/initiative_budgets';
import { authenticatePublicView, getPublicViewSso } from '@/features/canvas/api/public-view';
import { TimelineEditor as Editor } from '@/features/canvas/components/TimelineEditor';
import { EditorContextProvider, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { extractBudgetDataFromNodes } from '@/features/canvas/utils/extract-budget-from-snapshot';
import { getRoadmapById } from '@/features/roadmaps/api';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

interface ApiError {
  response?: {
    data?: {
      redirect?: string;
    };
  };
}

const PublicView = () => {
  const { roadmapPublicLinkUrl } = useParams<{ roadmapPublicLinkUrl: string }>();

  const [roadmapId, setRoadmapId] = useState<number | null>(null);
  const [defaultFilteredNodes, setDefaultFilteredNodes] = useState<components['schemas']['Node'][]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const [, setCookie] = useCookies(['redirect']);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const hasMountedRef = useRef(false);

  const { data, isLoading } = useQuery([getPublicViewSso.name], () =>
    getPublicViewSso(roadmapPublicLinkUrl ?? '')
  );

  const handlePublicView = (roadmap: components['schemas']['PublicViewRoadmap']) => {
    setRoadmapId(roadmap.id!);

    const formattedNodes = roadmap.nodes.map(n => ({
      ...n,
      initiatives: n.initiative!,
    }));
    setDefaultFilteredNodes(formattedNodes);

    // Pre-populate the query cache with snapshot data
    queryClient.setQueryData([getRoadmapById.name, roadmap.id], {
      ...roadmap,
      title: roadmap.titlePublic || roadmap.title,
    });

    queryClient.setQueryData(
      [getFiltersByRoadmapId.name, roadmap.id],
      roadmap.filters.map(f => {
        const filter = f as typeof f & { sortOrder?: number };
        return {
          ...f,
          nodes: f.nodeOptions,
          sortOrder: filter.sortOrder ?? 0,
          isVisible: f.isVisible ?? true,
        };
      })
    );

    queryClient.setQueryData([getAllRoadmapNodes.name, roadmap.id], formattedNodes);
    queryClient.setQueryData([getRoadmapTimePeriods.name, roadmap.id], roadmap.timePeriods);
    queryClient.setQueryData([getRoadmapSections.name, roadmap.id], roadmap.sections);
    queryClient.setQueryData([getRoadmapsRoadmapVisible.name, roadmap.id], roadmap.roadmapVisibles);
    queryClient.setQueryData([getRoadmapsPopupVisible.name, roadmap.id], roadmap.popupVisibles);
    queryClient.setQueryData([getRoadmapsDropdownVisible.name, roadmap.id], []);

    // Check if budgetRoadmapVisible data exists in snapshot
    // The backend sends this as budget_roadmap_visibles which becomes budgetRoadmapVisibles after camelCase conversion
    const roadmapWithBudgetVisible = roadmap as typeof roadmap & {
      budgetRoadmapVisible?: unknown[];
      budgetRoadmapVisibles?: unknown[];
      budget_roadmap_visibles?: unknown[];
    };

    // Set budgetRoadmapVisible data if it exists in snapshot
    // Check all possible property names
    const budgetVisibleData =
      roadmapWithBudgetVisible.budgetRoadmapVisibles ||
      roadmapWithBudgetVisible.budgetRoadmapVisible ||
      roadmapWithBudgetVisible.budget_roadmap_visibles;

    if (budgetVisibleData) {
      const queryKey = [getBudgetRoadmapVisible.name, Number(roadmap.id)];

      queryClient.setQueryData(queryKey, budgetVisibleData);
    } else {
      // Set an empty array to prevent errors
      const queryKey = [getBudgetRoadmapVisible.name, Number(roadmap.id)];
      queryClient.setQueryData(queryKey, []);
    }

    // ADD THIS: Extract and set budget data
    const budgetData = extractBudgetDataFromNodes(roadmap.nodes);
    queryClient.setQueryData([getAllBudgetsForRoadmap.name, roadmap.id], budgetData);

    setDataReady(true);
    setLoading(false);
  };

  const { mutateAsync: authenticatePublicViewMutation } = useMutation(authenticatePublicView, {
    onSuccess: handlePublicView,
    onError: (err: ApiError) => {
      const error = err as ApiError;
      if (error.response?.data?.redirect) {
        setCookie('redirect', `${pathname}`, { path: '/' });
        window.location.replace(error.response.data.redirect);
      } else {
        showToast('error', t('public-view.invalid_password'));
        setShowPasswordDialog(true);
      }
    },
  });

  const handlePasswordSubmit = async (password: string) => {
    if (password && roadmapPublicLinkUrl) {
      setShowPasswordDialog(false);
      setLoading(true);
      try {
        await authenticatePublicViewMutation({ password, roadmapPublicLinkUrl });
      } catch (err) {
        // Error handled by onError callback above
      }
    } else {
      showToast('error', t('public-view.invalid_password'));
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordDialog(false);
    navigate(routes.unauthorized);
  };

  useEffect(() => {
    const authorize = async () => {
      if (!roadmapPublicLinkUrl) return;

      try {
        if (data) {
          await authenticatePublicViewMutation({ roadmapPublicLinkUrl });
        } else {
          setShowPasswordDialog(true);
          setLoading(false);
        }
      } catch (err) {
        // Error handled by mutation onError callback
        setLoading(false);
      }
    };

    if (!hasMountedRef.current && !isLoading) {
      hasMountedRef.current = true;
      void authorize();
    }
  }, [
    isLoading,
    data,
    roadmapPublicLinkUrl,
    authenticatePublicViewMutation,
    pathname,
    setCookie,
    navigate,
    showToast,
    t,
  ]);

  return (
    <>
      <PasswordDialog
        open={showPasswordDialog}
        onSubmit={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
        title={t('public-view.enter_password')}
        message={t('public-view.password_required')}
      />
      {loading || !dataReady ? (
        <LoaderWrapper>
          <StyledLoader />
        </LoaderWrapper>
      ) : (
        roadmapId && (
          <EditorContextProvider
            isPublicView
            defaultFilteredNodes={defaultFilteredNodes}
            roadmapId={roadmapId}
          >
            <PublicViewEditor />
          </EditorContextProvider>
        )
      )}
    </>
  );
};

// Separate component to use the context
const PublicViewEditor = () => {
  const { updateLastSavedTime } = useEditorContext();

  useEffect(() => {
    updateLastSavedTime();
  }, [updateLastSavedTime]);

  return <Editor />;
};

const LoaderWrapper = styled.div`
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

export default PublicView;
